import React from "react";
// import "./CartTable.scss";
import { Table, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { buildPosPharmacyLineAmounts } from "../utils/billingUtils";

export const CartTable = ({
  t,
  cart,
  getCurrencySymbol,
  onBatchChange,
  onPriceChange,
  onQuantityChange,
  onTaxChange,
  onRemoveItem,
  disableScrollProps,
  itemTaxes,
  posTaxType,
  pharmacyStateCode,
  patientStateCode,
  posCompact,
  totals,
  highlightProductId,
  showPosTax = true,
  discountType = "Percentage",
  discountValue = 0,
}) => {
  const isSameState =
    !patientStateCode ||
    !pharmacyStateCode ||
    pharmacyStateCode === patientStateCode;

  const isGstMode = String(posTaxType || "").toLowerCase() === "gst";
  const taxColCount = showPosTax ? (isGstMode ? (isSameState ? 2 : 1) : 1) : 0;
  // 5 fixed cols + (Tax % + Discount + Taxable Value) + tax amount cols + Gross Total + remove
  const emptyColSpan = 5 + (showPosTax ? 3 + taxColCount : 0) + 2;

  // Calculate bill-level discount rate based on total taxable value
  const totalTaxableBeforeDiscount = cart.reduce((sum, item) => {
    const itemKey = `${item._id}-${item.selectedBatchId}`;
    const taxInfo = itemTaxes[itemKey];
    return sum + (taxInfo?.taxableAmount || 0);
  }, 0);

  const billDiscount = totals?.discount || 0;
  const billDiscountRate =
    totalTaxableBeforeDiscount > 0 ? billDiscount / totalTaxableBeforeDiscount : 0;

  // Display-only: the % the user entered, or the effective % for a fixed discount.
  const displayDiscountPct =
    discountType === "Percentage"
      ? Number(discountValue) || 0
      : Number((billDiscountRate * 100).toFixed(2));

  return (
    <div className="table-responsive table-wrap">
      <Table className="align-middle rounded shadow-sm table-bordered table-sm" style={{ fontSize: 'smaller' }}>

        <thead>
          <tr>
            <th scope="col" className="text-nowrap">{t("Batch Number")}</th>
            <th scope="col">{t("Product")}</th>
            <th scope="col">{t("Manufacturer")}</th>
            <th scope="col" className="text-center">{t("Price (MRP)")}</th>
            <th scope="col" className="text-center">{t("Qty")}</th>
            {showPosTax && (
              <>
                <th scope="col" className="text-center">{t("Discount")}</th>
                <th scope="col" className="text-center">{t("Taxable Value")}</th>
                <th scope="col" className="text-center">{t("Tax %")}</th>
                {isGstMode ? (
                  isSameState ? (
                    <>
                      <th scope="col" className="text-center">{t("CGST")}</th>
                      <th scope="col" className="text-center">{t("SGST")}</th>
                    </>
                  ) : (
                    <th scope="col" className="text-center">{t("IGST")}</th>
                  )
                ) : (
                  <th scope="col" className="text-center">{t("VAT")}</th>
                )}
              </>
            )}
            <th scope="col" className="text-center">{t("Gross Total")}</th>
            <th scope="col" className="text-center"></th>
          </tr>
        </thead>
        <tbody>
          {cart.map((item, rowIndex) => {
            const itemKey = `${item._id}-${item.selectedBatchId}`;
            const taxInfo = itemTaxes[itemKey] || {};
            const {
              taxRate,
              taxableAfterDiscount,
              rowCGST,
              rowSGST,
              rowIGST,
              rowVAT,
              grossTotal: itemGrossTotal,
            } = buildPosPharmacyLineAmounts({
              taxInfo,
              billDiscountRate,
              salePrice: item.salePrice || 0,
              quantity: item.quantity || 1,
              showPosTax,
            });
            const displayVAT = rowVAT;

            const isHighlighted =
              highlightProductId && item._id === highlightProductId;

            return (
              <tr
                key={itemKey}
                className={isHighlighted ? "pos-cart-row--scanned" : undefined}
              >
                <td>
                  {item.batchNumbers?.length > 0 ? (
                    <Form.Select
                      size="sm"
                      value={item.selectedBatchId}
                      onChange={(event) => onBatchChange(item._id, event.target.value)}
                    >
                      {item.batchList?.map((batch, index) => {
                        const isSelectedElsewhere = cart.some(
                          (ci, ciIndex) =>
                            ci._id === item._id &&
                            ci.selectedBatchId === batch._id &&
                            ciIndex !== rowIndex
                        );

                        return (
                          <option key={`${batch._id}-${index}`} value={batch._id} disabled={isSelectedElsewhere}>
                            {batch.batchNumber}
                            {showPosTax && batch.taxPercentage ? ` (${batch.taxPercentage}%)` : ""}
                            {isSelectedElsewhere ? " (Selected)" : ""}
                          </option>
                        );
                      })}
                    </Form.Select>
                  ) : (
                    <span>-</span>
                  )}
                </td>
                <td className="table-product-title" style={{ maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-saltName-${item._id}`}>
                      <div>
                        <strong>{item.brandName}</strong>
                        <br />
                        <strong>[{item.saltName}]</strong>
                      </div>
                    </Tooltip>}
                  >
                    <span>{item.brandName}</span>
                  </OverlayTrigger>
                </td>
                <td className="table-product-title" style={{ maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${item._id}`}>{item.manufacturer}</Tooltip>}
                  >
                    <span>{item.manufacturer}</span>
                  </OverlayTrigger>
                </td>
                <td>
                  <div className="input-group custom-qty-input table-price-counter-nowrap">
                    <button
                      type="button"
                      className="button-minus icon-shape icon-sm btn-danger"
                      onClick={() =>
                        onPriceChange(item._id, item.selectedBatchId, "salePrice", Math.max(0, (item.salePrice || 0) - 0.01))
                      }
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={item.salePrice || 0}
                      step="0.01"
                      className="border-0 quantity-field incrementor"
                      onChange={(event) =>
                        onPriceChange(item._id, item.selectedBatchId, "salePrice", event.target.value)
                      }
                      {...disableScrollProps}
                    />
                    <button
                      type="button"
                      className="button-plus icon-shape icon-sm btn-primary"
                      onClick={() =>
                        onPriceChange(item._id, item.selectedBatchId, "salePrice", (item.salePrice || 0) + 0.01)
                      }
                    >
                      +
                    </button>
                  </div>
                </td>
                <td>
                  <div className="input-group custom-qty-input table-price-counter-nowrap">
                    <button
                      type="button"
                      className="button-minus icon-shape icon-sm btn-danger"
                      onClick={() =>
                        onQuantityChange(item._id, item.selectedBatchId, Math.max(1, (item.quantity || 1) - 1))
                      }
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={item.quantity || 1}
                      className="border-0 quantity-field incrementor"
                      min={1}
                      max={item.maxQuantity}
                      onChange={(event) =>
                        onQuantityChange(item._id, item.selectedBatchId, Number(event.target.value))
                      }
                      {...disableScrollProps}
                    />
                    <button
                      type="button"
                      className="button-plus icon-shape icon-sm btn-primary"
                      onClick={() =>
                        onQuantityChange(item._id, item.selectedBatchId, (item.quantity || 1) + 1)
                      }
                    >
                      +
                    </button>
                  </div>
                  <div style={{ fontSize: "0.8em", color: "#888", textAlign: "center" }}>
                    {t("Stock")}: <strong>{item.maxQuantity || 0}</strong>
                  </div>
                </td>
                {showPosTax && (
                  <>
                    <td className={`text-center${displayDiscountPct > 0 ? " text-danger" : ""}`}>
                      {displayDiscountPct}%
                    </td>
                    <td className="text-center fw-bold">
                      {getCurrencySymbol()}{taxableAfterDiscount.toFixed(2)}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={item.taxPercentage ?? taxRate ?? 0}
                        min={0}
                        max={100}
                        step="0.01"
                        className="form-control form-control-sm text-center fw-bold no-spinner"
                        onChange={(event) =>
                          onTaxChange(item._id, item.selectedBatchId, event.target.value)
                        }
                        {...disableScrollProps}
                      />
                    </td>
                    {isGstMode ? (
                      isSameState ? (
                        <>
                          <td className="text-center text-success">
                            {getCurrencySymbol()}{rowCGST.toFixed(2)}
                          </td>
                          <td className="text-center text-success">
                            {getCurrencySymbol()}{rowSGST.toFixed(2)}
                          </td>
                        </>
                      ) : (
                        <td className="text-center text-success">
                          {getCurrencySymbol()}{rowIGST.toFixed(2)}
                        </td>
                      )
                    ) : (
                      <td className="text-center text-success">
                        {getCurrencySymbol()}{displayVAT.toFixed(2)}
                      </td>
                    )}
                  </>
                )}
                <td className="text-center fw-bold" style={{ fontSize: 'small' }}>
                  {getCurrencySymbol()}{itemGrossTotal.toFixed(2)}
                </td>
                <td className="text-center">
                  <button
                    className="btn-danger btn-sm remove-item"
                    onClick={() => onRemoveItem(item._id, item.selectedBatchId)}
                  >
                    &times;
                  </button>
                </td>
              </tr>
            );
          })}
          {cart.length === 0 && (
            <tr>
              <td colSpan={emptyColSpan} className="text-center text-muted">
                {t("No items in cart")}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default CartTable;
